import { motion } from 'framer-motion'
import {
  FaUserFriends,
  FaFileAlt,
  FaCalendarCheck,
  FaFileInvoiceDollar,
  FaChartLine,
  FaLock,
  FaMobile,
  FaCloud,
  FaBell,
  FaUserCog,
  FaClipboardList,
  FaSync
} from 'react-icons/fa'

const FeaturesPage = () => {
  const featureCategories = [
    {
      category: 'Client Management',
      icon: <FaUserFriends className="w-12 h-12" />,
      features: [
        {
          title: 'Comprehensive Client Profiles',
          description: 'Store all client information including contact details, demographics, emergency contacts, and custom fields.'
        },
        {
          title: 'Client Status Tracking',
          description: 'Track client status (Active, Inactive, Pending, Discharged) with automatic status updates.'
        },
        {
          title: 'Profile Photos & Documents',
          description: 'Upload client photos and attach relevant documents securely in the cloud.'
        },
        {
          title: 'Advanced Search & Filtering',
          description: 'Quickly find clients with powerful search and filter options by status, therapist, or custom criteria.'
        },
        {
          title: 'Bulk Import',
          description: 'Import existing client lists from CSV files to get started quickly.'
        }
      ]
    },
    {
      category: 'Session Notes',
      icon: <FaFileAlt className="w-12 h-12" />,
      features: [
        {
          title: 'Customizable Templates',
          description: 'Create custom note templates tailored to your practice style and requirements.'
        },
        {
          title: 'Structured Assessments',
          description: 'Document appearance, mood, affect, interventions, and treatment plans with guided fields.'
        },
        {
          title: 'Privacy Levels',
          description: 'Set privacy levels (Public, Restricted, Confidential) for different types of notes.'
        },
        {
          title: 'Version History',
          description: 'Track all changes to session notes with full audit trails for compliance.'
        },
        {
          title: 'Clinical Supervisor Review',
          description: 'Built-in workflow for supervisors to review and approve session notes.'
        }
      ]
    },
    {
      category: 'Scheduling & Appointments',
      icon: <FaCalendarCheck className="w-12 h-12" />,
      features: [
        {
          title: 'Visual Calendar',
          description: 'Beautiful calendar views (day, week, month) to manage your schedule at a glance.'
        },
        {
          title: 'Recurring Appointments',
          description: 'Set up weekly, bi-weekly, or monthly recurring sessions automatically.'
        },
        {
          title: 'Calendar Sync',
          description: 'Two-way sync with Google Calendar and Microsoft Outlook.'
        },
        {
          title: 'Automated Reminders',
          description: 'Send email and SMS reminders to reduce no-shows and late cancellations.'
        },
        {
          title: 'Staff Availability',
          description: 'Manage practitioner schedules and availability across your team.'
        }
      ]
    },
    {
      category: 'Billing & Invoicing',
      icon: <FaFileInvoiceDollar className="w-12 h-12" />,
      features: [
        {
          title: 'Professional Invoices',
          description: 'Generate branded invoices with line items, taxes, and custom payment terms.'
        },
        {
          title: 'Payment Tracking',
          description: 'Track payment status (Paid, Pending, Overdue) with automatic reminders.'
        },
        {
          title: 'Expense Management',
          description: 'Upload receipts and track practice expenses for tax purposes.'
        },
        {
          title: 'Revenue Reports',
          description: 'View revenue trends, outstanding invoices, and financial summaries.'
        },
        {
          title: 'Stripe Integration',
          description: 'Accept credit card payments securely with built-in Stripe integration.'
        }
      ]
    },
    {
      category: 'Analytics & Reporting',
      icon: <FaChartLine className="w-12 h-12" />,
      features: [
        {
          title: 'Practice Dashboard',
          description: 'Get a comprehensive overview of your practice metrics at a glance.'
        },
        {
          title: 'Client Growth Tracking',
          description: 'Monitor client acquisition, retention, and discharge trends over time.'
        },
        {
          title: 'Session Statistics',
          description: 'Track total sessions, average session duration, and session completion rates.'
        },
        {
          title: 'Revenue Analytics',
          description: 'Visualize revenue trends, payment rates, and financial forecasts.'
        },
        {
          title: 'Custom Reports',
          description: 'Export custom reports for business analysis or insurance purposes.'
        }
      ]
    },
    {
      category: 'Security & Compliance',
      icon: <FaLock className="w-12 h-12" />,
      features: [
        {
          title: 'HIPAA Compliance',
          description: 'Enterprise-grade security with encryption, audit logs, and compliance features.'
        },
        {
          title: 'Role-Based Access',
          description: 'Control who can view, edit, or delete sensitive information with granular permissions.'
        },
        {
          title: 'Audit Logging',
          description: 'Track all user actions with detailed audit logs for compliance requirements.'
        },
        {
          title: 'Data Encryption',
          description: 'All data encrypted at rest and in transit with industry-standard protocols.'
        },
        {
          title: 'Automatic Backups',
          description: 'Daily automated backups with 30-day retention and point-in-time recovery.'
        }
      ]
    }
  ]

  const additionalFeatures = [
    {
      icon: <FaMobile className="w-8 h-8" />,
      title: 'Mobile Responsive',
      description: 'Access your practice from any device - desktop, tablet, or phone.'
    },
    {
      icon: <FaCloud className="w-8 h-8" />,
      title: 'Cloud Storage',
      description: 'Secure cloud storage for documents, photos, and files with unlimited capacity.'
    },
    {
      icon: <FaBell className="w-8 h-8" />,
      title: 'Real-time Notifications',
      description: 'Get instant notifications for appointments, messages, and important updates.'
    },
    {
      icon: <FaUserCog className="w-8 h-8" />,
      title: 'Multi-User Support',
      description: 'Collaborate with team members with different roles and permissions.'
    },
    {
      icon: <FaClipboardList className="w-8 h-8" />,
      title: 'Risk Assessments',
      description: 'Conduct and track clinical risk assessments with guided workflows.'
    },
    {
      icon: <FaSync className="w-8 h-8" />,
      title: 'Automatic Updates',
      description: 'Always get the latest features and security updates automatically.'
    }
  ]

  // Helper function to render feature-specific mockups
  const renderFeatureMockup = (categoryName) => {
    switch (categoryName) {
      case 'Client Management':
        return (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white/20">
            <div className="bg-gradient-to-br from-gray-100 to-gray-50 p-1">
              <div className="bg-white rounded-2xl p-8 md:p-12">
                {/* Browser Header */}
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex-1 text-center text-sm text-gray-500">Clients Directory</div>
                  </div>
                </div>

                {/* Client List Mock */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900">Your Clients</h4>
                    <input type="text" placeholder="Search clients..." className="text-sm border border-gray-200 rounded px-3 py-1 w-40" />
                  </div>

                  {[
                    { name: 'Sarah Mitchell', status: 'Active', sessions: 12, phone: '(555) 123-4567' },
                    { name: 'James Chen', status: 'Active', sessions: 8, phone: '(555) 234-5678' },
                    { name: 'Maria Garcia', status: 'Active', sessions: 15, phone: '(555) 345-6789' }
                  ].map((client, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{client.name}</p>
                        <p className="text-xs text-gray-500">{client.phone}</p>
                      </div>
                      <div className="text-right">
                        <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">{client.status}</span>
                        <p className="text-xs text-gray-500 mt-1">{client.sessions} sessions</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )

      case 'Session Notes':
        return (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white/20">
            <div className="bg-gradient-to-br from-gray-100 to-gray-50 p-1">
              <div className="bg-white rounded-2xl p-8 md:p-12">
                {/* Browser Header */}
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex-1 text-center text-sm text-gray-500">New Session Note</div>
                  </div>
                </div>

                {/* Session Note Form Mock */}
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Client</label>
                    <input type="text" value="Sarah Mitchell" className="w-full text-sm border border-gray-200 rounded px-3 py-2 mt-1 bg-gray-50" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Session Date</label>
                      <input type="date" className="w-full text-sm border border-gray-200 rounded px-3 py-2 mt-1 bg-gray-50" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-600">Duration (min)</label>
                      <input type="text" value="50" className="w-full text-sm border border-gray-200 rounded px-3 py-2 mt-1 bg-gray-50" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Session Notes</label>
                    <textarea className="w-full text-sm border border-gray-200 rounded px-3 py-2 mt-1 bg-gray-50" rows="4" placeholder="Document your session..."></textarea>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Privacy Level</label>
                    <select className="w-full text-sm border border-gray-200 rounded px-3 py-2 mt-1 bg-gray-50">
                      <option>Confidential</option>
                      <option>Restricted</option>
                      <option>Public</option>
                    </select>
                  </div>
                  <button className="w-full bg-emerald-600 text-white text-sm font-medium py-2 rounded hover:bg-emerald-700">Save Session Note</button>
                </div>
              </div>
            </div>
          </div>
        )

      case 'Scheduling & Appointments':
        return (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white/20">
            <div className="bg-gradient-to-br from-gray-100 to-gray-50 p-1">
              <div className="bg-white rounded-2xl p-8 md:p-12">
                {/* Browser Header */}
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex-1 text-center text-sm text-gray-500">Calendar & Appointments</div>
                  </div>
                </div>

                {/* Calendar Mock */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-900">November 2025</h4>
                    <div className="space-x-2">
                      <button className="text-sm border border-gray-200 rounded px-2 py-1 hover:bg-gray-50">&lt;</button>
                      <button className="text-sm border border-gray-200 rounded px-2 py-1 hover:bg-gray-50">&gt;</button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-2 text-center text-xs">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                      <div key={day} className="font-medium text-gray-600 py-2">{day}</div>
                    ))}
                    {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20].map((day) => (
                      <div key={day} className={`py-2 rounded ${day === 15 ? 'bg-emerald-100 text-emerald-700 font-bold' : 'text-gray-600'}`}>{day}</div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 pt-4 mt-4">
                    <p className="text-xs font-semibold text-gray-600 mb-2">Upcoming Appointments</p>
                    {[
                      { time: '10:00 AM', client: 'Sarah Mitchell' },
                      { time: '2:00 PM', client: 'James Chen' },
                      { time: '4:30 PM', client: 'Maria Garcia' }
                    ].map((apt, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded mb-2 border border-gray-100">
                        <div className="text-sm font-medium text-emerald-600 min-w-fit">{apt.time}</div>
                        <div className="text-sm text-gray-900">{apt.client}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )

      case 'Billing & Invoicing':
        return (
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white/20">
            <div className="bg-gradient-to-br from-gray-100 to-gray-50 p-1">
              <div className="bg-white rounded-2xl p-8 md:p-12">
                {/* Browser Header */}
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex-1 text-center text-sm text-gray-500">Invoices</div>
                  </div>
                </div>

                {/* Invoice List Mock */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <h4 className="font-semibold text-gray-900">Recent Invoices</h4>
                    <button className="text-sm bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700">+ New Invoice</button>
                  </div>

                  {[
                    { id: 'INV-001', client: 'Sarah Mitchell', amount: '$150.00', status: 'Paid', date: 'Nov 18' },
                    { id: 'INV-002', client: 'James Chen', amount: '$100.00', status: 'Pending', date: 'Nov 15' },
                    { id: 'INV-003', client: 'Maria Garcia', amount: '$200.00', status: 'Pending', date: 'Nov 12' }
                  ].map((invoice, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{invoice.id}</p>
                        <p className="text-xs text-gray-500">{invoice.client}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{invoice.amount}</p>
                        <span className={`inline-block text-xs font-medium px-2 py-1 rounded mt-1 ${
                          invoice.status === 'Paid'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {invoice.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-600 via-primary-700 to-primary-900 py-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-20"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Powerful Features for Your Practice
            </h1>
            <p className="text-lg md:text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Everything you need to manage your wellness practice efficiently,
              all in one intuitive platform.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Features */}
      {featureCategories.map((category, categoryIndex) => (
        <section
          key={categoryIndex}
          className={`section-padding ${categoryIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        >
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex items-center justify-center mb-12"
            >
              <div className="text-primary-600 mr-4">{category.icon}</div>
              <h2 className="text-4xl font-bold text-gray-900">
                {category.category}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {category.features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Feature Mockup */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              {renderFeatureMockup(category.category)}
            </motion.div>
          </div>
        </section>
      ))}

      {/* Additional Features - Dark with Gradient */}
      <section className="section-padding bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-600 rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-600 rounded-full blur-3xl opacity-10"></div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-4 leading-tight tracking-tight">
              And So Much More...
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Additional features to make your practice run smoothly
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-gray-800 to-gray-700 rounded-xl p-8 border border-gray-600 hover:border-primary-500 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/20"
              >
                <div className="text-primary-400 text-3xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed text-base">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Bold Dark */}
      <section className="section-padding bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary-600 rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-accent-600 rounded-full blur-3xl opacity-20"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-12 md:p-16 border-2 border-white/20"
          >
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight">
              Ready to Experience These Features?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              Start your free 30-day trial today. No credit card required.
            </p>
            <a href="https://acuitypractice.com" className="btn-primary text-lg px-10 py-4 hover:scale-105 transition-transform duration-300">
              Start Free Trial
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default FeaturesPage
